import { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { useFilterData } from '../../../../../utilities/hooks/useFilterData';
import useFilters from '../../../../../utilities/hooks/useFilters';
import { getAllBenchmarkDetails } from '../../../../../utilities/common/utils';
import { SelectedBenchmark } from '../../../../../shared/constants/filter/filter';
import { BarChartMetaData } from '../../../../../shared/types/widget';
import {
  ConfigDataType,
  IBenchmarkType,
} from '../../../../../shared/types/filters';

type TooltipSource = {
  meta: BarChartMetaData;
  score?: number;
  uniqueRespondents?: number;
  title?: string;
};

type TooltipInfo = {
  title?: string;
  lines: string[];
};

export function buildTooltip({
  meta,
  score = 0,
  uniqueRespondents = 0,
  title,
  benchmarkType,
  configData,
  t,
}: TooltipSource & {
  benchmarkType: IBenchmarkType;
  configData: ConfigDataType;
  t: (key: string, options?: any) => string;
}): TooltipInfo {
  const { thriving, attention, global } = getAllBenchmarkDetails(
    meta?.meta,
    benchmarkType,
    configData,
  );

  const lines = [
    `${t('SCORE')}: ${score}`,
    `${t('UNIQUE_RESPONDENTS')}: ${uniqueRespondents}`,
    `${benchmarkType === SelectedBenchmark.GLOBAL ? t('GLOBAL') : t('INDUSTRY')} ${t('AVERAGE')}: ${global.value}`,
    `${t('THRIVING_CUTOFF')}: ${thriving.value}`,
    `${t('NEEDS_ATTENTION_CUTOFF')}: ${attention.value}`,
  ];

  return { title, lines };
}

export function useTooltipInfo(props: TooltipSource): TooltipInfo {
  const { benchmarkType } = useFilters();
  const { configData } = useFilterData();
  const { t } = useTranslation();

  if (!configData) {
    throw new Error('configData is undefined. Tooltip cannot be built.');
  }

  return buildTooltip({ ...props, benchmarkType, configData, t });
}

// Render tooltip via JSX
export function TooltipContentJSX(props: TooltipSource): JSX.Element {
  const { title, lines } = useTooltipInfo(props);
  const { t } = useTranslation();

  return (
    <div className="chart-tooltip-vertical-large">
      {title && <span className="block">{title}</span>}
      {lines.map((line, i) => (
        <p key={line}>{t(line)}</p>
      ))}
    </div>
  );
}

// DOM Tooltip (for Chart.js external plugin)
export function TooltipContentDOM(
  props: TooltipSource & {
    benchmarkType: IBenchmarkType;
    configData: ConfigDataType;
    t: any;
  },
): HTMLDivElement {
  const { title, lines } = buildTooltip(props);

  const div = document.createElement('div');
  div.classList.add('chart-tooltip-vertical-large');

  if (title) {
    const span = document.createElement('span');
    span.textContent = title;
    div.appendChild(span);
  }

  lines.forEach((line) => {
    const p = document.createElement('p');
    p.textContent = line;
    div.appendChild(p);
  });

  return div;
}
